import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Check } from "lucide-react";
import { Stage } from "@/components/Stage";
import { Eyebrow } from "@/components/Wordmark";

const EASE = [0.22, 1, 0.36, 1];
const usd = (n) => "$" + Math.round(n).toLocaleString("en-US");

const flatTier = (c) =>
  c <= 10 ? 2000 : c <= 30 ? 3000 : c <= 75 ? 5000 : c <= 150 ? 6000 : 7000;

const PlanCard = ({ featured, name, price, tag, bullets }) => (
  <div
    className={`rounded-2xl p-5 bg-[#FBFAF6] ${
      featured ? "border-2 border-accent" : "border border-hairline"
    }`}
  >
    <div className="flex items-baseline justify-between">
      <h3 className="font-display font-medium text-lg text-ink">{name}</h3>
      {featured ? (
        <span className="font-mono text-[9px] uppercase tracking-[0.14em] text-accent border border-accent/40 rounded-full px-2 py-0.5">
          Featured
        </span>
      ) : null}
    </div>
    <div className="font-display font-semibold text-2xl text-ink mt-1.5">{price}</div>
    <p className="font-sans text-[12px] text-ink-soft mt-1.5">{tag}</p>
    <div className="mt-3 space-y-1.5">
      {bullets.map((b, i) => (
        <div key={i} className="flex items-start gap-2">
          <Check className="w-3.5 h-3.5 text-accent mt-0.5 shrink-0" strokeWidth={2.5} />
          <span className="font-sans text-[12px] text-ink-soft leading-snug">{b}</span>
        </div>
      ))}
    </div>
  </div>
);

export const PricingSlide = () => {
  const reduce = useReducedMotion();
  const [cases, setCases] = useState(35);

  const flat = flatTier(cases);
  const perCase = 200 * cases;
  const barMax = Math.max(flat, perCase);
  const savings = (perCase - flat) * 12;
  const pct = ((cases - 5) / (200 - 5)) * 100;

  const container = {
    hidden: {},
    show: { transition: { staggerChildren: reduce ? 0 : 0.1, delayChildren: 0.05 } },
  };
  const item = {
    hidden: { opacity: 0, y: reduce ? 0 : 18 },
    show: { opacity: 1, y: 0, transition: { duration: 0.65, ease: EASE } },
  };

  return (
    <Stage testId="appendix-pricing">
      <motion.div
        variants={container}
        initial="hidden"
        animate="show"
        className="h-full w-full flex flex-col px-[clamp(28px,6%,84px)] py-[clamp(18px,3.6%,40px)]"
      >
        <motion.div variants={item} className="flex items-center justify-between">
          <Eyebrow>Appendix · Pricing</Eyebrow>
          <span className="font-mono text-xs tracking-[0.24em] text-accent tabular-nums">
            A · 1 of 3
          </span>
        </motion.div>

        <motion.h1
          variants={item}
          className="font-display font-medium text-3xl sm:text-4xl leading-[1.06] text-ink mt-3"
        >
          Pricing that doesn't punish growth.
        </motion.h1>
        <motion.p variants={item} className="font-sans text-[14px] text-ink-soft mt-2">
          Two simple ways to buy — and neither charges you more for every case you win.
        </motion.p>

        <div className="flex-1 min-h-0 grid grid-cols-1 min-[900px]:grid-cols-2 gap-6 mt-5">
          {/* LEFT plans */}
          <motion.div variants={item} className="flex flex-col gap-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <PlanCard
                featured
                name="Flat monthly"
                price="$2K–$5K/mo"
                tag="Predictable. Scales with your firm, not per case."
                bullets={[
                  "All in-scope agents included",
                  "Unlimited within fair use",
                  "Best for steady, growing volume",
                ]}
              />
              <PlanCard
                name="Per-minute usage"
                price="$0.50/min"
                tag="Pay only for what you use."
                bullets={[
                  "No monthly minimum to start",
                  "Flexes with call volume",
                  "Best for variable or seasonal volume",
                ]}
              />
            </div>
            <div className="flex items-start gap-3 rounded-xl border border-accent/20 bg-accent/[0.06] px-4 py-3">
              <span className="text-accent text-base leading-none mt-0.5">◆</span>
              <p className="font-sans text-[12px] text-ink-soft leading-snug">
                Pilot before you commit — the pilot fee is often waived for a
                committed pilot.
              </p>
            </div>
          </motion.div>

          {/* RIGHT contrast panel */}
          <motion.div
            variants={item}
            className="rounded-2xl p-5 flex flex-col text-paper bg-[linear-gradient(155deg,#191622_0%,#211D2A_100%)]"
          >
            <h3 className="font-display font-medium text-lg text-paper">
              Flat vs. per-case, as you grow
            </h3>
            <p className="font-sans text-[12px] text-paper/60 mt-1">
              Per-case pricing quietly taxes every win. Flat pricing doesn't.
            </p>

            <div className="flex items-baseline justify-between mt-4 mb-2">
              <span className="font-sans text-[13px] text-paper/80">Signed cases / month</span>
              <span className="font-mono text-sm text-[#93B4FF] tabular-nums">{cases}</span>
            </div>
            <input
              data-testid="pricing-slider"
              type="range"
              min={5}
              max={200}
              step={5}
              value={cases}
              onChange={(e) => setCases(Number(e.target.value))}
              aria-label="Signed cases per month"
              className="range-lex"
              style={{
                background: `linear-gradient(to right, #1E5BFF 0%, #1E5BFF ${pct}%, rgba(255,255,255,0.14) ${pct}%, rgba(255,255,255,0.14) 100%)`,
              }}
            />

            <div className="mt-5 space-y-3">
              <div>
                <div className="flex justify-between font-mono text-[11px] mb-1">
                  <span className="text-[#93B4FF]">Lexafide · flat</span>
                  <span className="text-paper tabular-nums">{usd(flat)}/mo</span>
                </div>
                <div className="h-3 rounded-full bg-white/10 overflow-hidden">
                  <div
                    data-testid="bar-flat"
                    className="h-full bg-accent transition-[width] duration-500 ease-out"
                    style={{ width: `${(flat / barMax) * 100}%` }}
                  />
                </div>
              </div>
              <div>
                <div className="flex justify-between font-mono text-[11px] mb-1">
                  <span className="text-[#E88] ">Per-case model · $200/case</span>
                  <span className="text-paper tabular-nums">{usd(perCase)}/mo</span>
                </div>
                <div className="h-3 rounded-full bg-white/10 overflow-hidden">
                  <div
                    data-testid="bar-percase"
                    className="h-full bg-[#C0453A] transition-[width] duration-500 ease-out"
                    style={{ width: `${(perCase / barMax) * 100}%` }}
                  />
                </div>
              </div>
            </div>

            <p
              data-testid="pricing-savings"
              className="font-sans text-[13px] text-paper/85 leading-snug mt-auto pt-5"
            >
              {savings > 0 ? (
                <>
                  At {cases} signed cases a month, flat pricing keeps{" "}
                  <span className="text-[#93B4FF] font-medium">{usd(savings)}/yr</span> in
                  your pocket versus a per-case model.
                </>
              ) : (
                <>
                  At low volume the two are close — flat pricing pulls ahead as you
                  grow.
                </>
              )}
            </p>
          </motion.div>
        </div>

        <motion.p
          variants={item}
          className="font-sans text-[11px] text-ink-muted leading-relaxed mt-4 pt-3 border-t border-hairline max-w-4xl"
        >
          Figures shown are working estimates from current deals — confirm and adjust
          before sharing externally. 'Per-case model' is an illustrative comparison,
          not a specific competitor. Final pricing is set during discovery.
        </motion.p>
      </motion.div>
    </Stage>
  );
};

export default PricingSlide;
